import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { RegisterSessionApi } from "@/redux/services/pos/registerSession.api";
import {
  Expense,
  ExpenseCategory,
  ExpensePaymentMethod,
  FinancialOverviewResponse,
} from "./expense.type";

export interface ExpenseWritePayload {
  category: string; // IRI, e.g. /api/expense_categories/{uuid}
  amount: number; // integer cents
  paymentMethod: ExpensePaymentMethod;
  paidFromDrawer?: boolean;
  incurredAt: string; // YYYY-MM-DD
  payee?: string | null;
  description?: string | null;
  receipt?: string | null; // MediaObject IRI
}

// API Platform bracket syntax is passed literally in the query string.
export interface GetExpensesParams {
  page?: number;
  limit?: number;
  status?: string;
  paymentMethod?: string;
  "category.uuid"?: string;
  "incurredAt[after]"?: string;
  "incurredAt[before]"?: string;
  "order[incurredAt]"?: "asc" | "desc";
}

export interface ExpenseCategoryWritePayload {
  label: string;
}

export interface ExpenseCategoryUpdatePayload {
  label?: string;
  archived?: boolean;
}

export const ExpenseApi = createApi({
  reducerPath: "expenseApi",
  tagTypes: ["expense", "expense-category"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getExpenses: build.query<
      PaginationResults<Expense>,
      { restaurantId: string; query?: GetExpensesParams }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/expenses`,
        method: "GET",
        params: query,
      }),
      providesTags: ["expense"],
      transformResponse: (data: CollectionData<Expense>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getExpense: build.query<Expense, { expenseUuid: string }>({
      query: ({ expenseUuid }) => `expenses/${expenseUuid}`,
      providesTags: ["expense"],
    }),

    createExpense: build.mutation<
      Expense,
      { restaurantId: string; data: ExpenseWritePayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/expenses`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["expense"],
      // A drawer-backed expense moves the open cash session; refresh it.
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (data.paidFromDrawer) {
            dispatch(
              RegisterSessionApi.util.invalidateTags([
                "registerSession",
                "registerSessionLedger",
              ])
            );
          }
        } catch {
          /* the mutation error is surfaced by the caller */
        }
      },
    }),

    voidExpense: build.mutation<
      Expense,
      { expenseUuid: string; voidReason?: string; drawerBacked?: boolean }
    >({
      query: ({ expenseUuid, voidReason }) => ({
        url: `expenses/${expenseUuid}/void`,
        method: "POST",
        body: { voidReason },
      }),
      invalidatesTags: ["expense"],
      async onQueryStarted({ drawerBacked }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (drawerBacked) {
            dispatch(
              RegisterSessionApi.util.invalidateTags([
                "registerSession",
                "registerSessionLedger",
              ])
            );
          }
        } catch {
          /* handled by the caller */
        }
      },
    }),

    getExpenseCategories: build.query<
      ExpenseCategory[],
      { restaurantId: string }
    >({
      // A restaurant's categories (system + custom + archived) are a small
      // set, but can exceed the API's default page of 30: fetch them all
      // (SFRESTO-137).
      query: ({ restaurantId }) => ({
        url: `restaurants/${restaurantId}/expense_categories`,
        method: "GET",
        params: { pagination: false },
      }),
      providesTags: ["expense-category"],
      transformResponse: (data: CollectionData<ExpenseCategory>) =>
        data["hydra:member"],
    }),

    createExpenseCategory: build.mutation<
      ExpenseCategory,
      { restaurantId: string; data: ExpenseCategoryWritePayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/expense_categories`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["expense-category"],
    }),

    updateExpenseCategory: build.mutation<
      ExpenseCategory,
      { categoryUuid: string; data: ExpenseCategoryUpdatePayload }
    >({
      query: ({ categoryUuid, data }) => ({
        url: `expense_categories/${categoryUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["expense-category", "expense"],
    }),

    getFinancialOverview: build.query<
      FinancialOverviewResponse,
      { startDate: string; endDate: string; compare?: boolean }
    >({
      query: ({ startDate, endDate, compare }) => ({
        url: "dashboard/financial_overview",
        method: "GET",
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(compare ? { compare: true } : {}),
        },
      }),
      providesTags: ["expense"],
    }),
  }),
});

export const {
  useGetExpensesQuery,
  useGetExpenseQuery,
  useCreateExpenseMutation,
  useVoidExpenseMutation,
  useGetExpenseCategoriesQuery,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
  useGetFinancialOverviewQuery,
} = ExpenseApi;
